import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2, AlertCircle, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import {
    getPushStatus,
    registerPushSubscription,
    unsubscribePush,
    updatePushCategories,
} from "../lib/webPush";
import api from "../lib/api";

function deviceLabel(ua = "") {
    const os = /Android/i.test(ua) ? "Android" : /iPhone|iPad/i.test(ua) ? "iOS"
        : /Windows/i.test(ua) ? "Windows" : /Mac/i.test(ua) ? "Mac" : "Gerät";
    const browser = /Edg\//.test(ua) ? "Edge" : /Firefox/i.test(ua) ? "Firefox"
        : /Chrome/i.test(ua) ? "Chrome" : /Safari/i.test(ua) ? "Safari" : "Browser";
    return `${browser} auf ${os}`;
}

const CATEGORY_LABELS = [
    { key: "messages", label: "Nachrichten", desc: "Neue Chat-Nachrichten zu Inseraten" },
    { key: "offers", label: "Preisangebote", desc: "Neue Angebote, Counter, Annahmen" },
    { key: "tradein", label: "Inzahlungnahme", desc: "Trade-In-Anfragen und Updates" },
    { key: "buyrequests", label: "Kaufgesuche", desc: "Eingehende Buy-Requests" },
    { key: "saved_search", label: "Suchaufträge", desc: "Treffer Ihrer gespeicherten Suchen" },
    { key: "vermittler", label: "Vermittler", desc: "Punkte-Updates und Auszahlungen" },
    { key: "reviews", label: "Bewertungen", desc: "Neue Kundenbewertungen — besonders niedrige Sterne" },
];

/**
 * UI to enable/disable Web-Push notifications + per-category opt-out.
 * Independent of email/SMS settings — works for ANY logged-in user.
 */
export default function PushSettingsSection() {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState({ supported: false, permission: "default", subscribed: false });
    const [vapidConfigured, setVapidConfigured] = useState(false);
    const [disabledCats, setDisabledCats] = useState([]);
    const [devices, setDevices] = useState([]);

    const refresh = async () => {
        try {
            const [st, vapid] = await Promise.all([
                getPushStatus(),
                api.get("/push/public-key").catch(() => ({ data: { configured: false } })),
            ]);
            setStatus(st);
            setVapidConfigured(!!vapid.data?.configured);
            try {
                const r = await api.get("/push/me");
                const items = r.data.items || [];
                setDevices(items);
                if (st.subscribed) {
                    const mine = items.find((s) => s.endpoint === st.endpoint);
                    setDisabledCats(mine?.disabled_categories || []);
                }
            } catch { /* silent */ }
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const enable = async () => {
        setBusy(true);
        const res = await registerPushSubscription();
        if (res.ok) {
            toast.success("Push-Benachrichtigungen aktiviert");
            await refresh();
        } else if (res.reason === "denied") {
            toast.error("Berechtigung verweigert — bitte im Browser-Symbol freigeben");
        } else if (res.reason === "unsupported") {
            toast.error("Ihr Browser unterstützt keine Push-Benachrichtigungen");
        } else if (res.reason === "vapid_not_configured") {
            toast.error("Push-Server ist noch nicht konfiguriert");
        } else {
            toast.error("Aktivierung fehlgeschlagen");
        }
        setBusy(false);
    };

    const disable = async () => {
        if (!window.confirm("Push-Benachrichtigungen wirklich deaktivieren?")) return;
        setBusy(true);
        const res = await unsubscribePush();
        if (res.ok) {
            toast.success("Push-Benachrichtigungen deaktiviert");
            await refresh();
        } else {
            toast.error("Deaktivierung fehlgeschlagen");
        }
        setBusy(false);
    };

    const sendTest = async () => {
        setBusy(true);
        try {
            const r = await api.post("/push/test");
            if (r.data?.sent > 0) {
                toast.success(`Testbenachrichtigung an ${r.data.sent} Gerät(e) gesendet`);
            } else {
                toast.error("Keine Zustellung möglich — kein aktives Geräte-Abo gefunden");
            }
        } catch {
            toast.error("Test fehlgeschlagen");
        }
        setBusy(false);
    };

    const removeDevice = async (endpoint) => {
        if (!window.confirm("Dieses Gerät aus den Push-Benachrichtigungen entfernen?")) return;
        try {
            await api.delete("/push/subscribe", { data: { endpoint } });
            toast.success("Gerät entfernt — es erhält keine Benachrichtigungen mehr");
            await refresh();
        } catch {
            toast.error("Entfernen fehlgeschlagen");
        }
    };

    const toggleCategory = async (key, currentlyEnabled) => {
        const next = currentlyEnabled
            ? [...disabledCats, key]                  // disable category → add to opt-out list
            : disabledCats.filter((c) => c !== key);  // enable → remove from opt-out list
        setDisabledCats(next);
        try {
            await updatePushCategories(next);
        } catch (e) {
            toast.error("Einstellung konnte nicht gespeichert werden");
            await refresh();
        }
    };

    if (loading) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="push-settings-loading">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!status.supported) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="push-settings-unsupported">
                <div className="flex items-center gap-2 mb-1">
                    <BellOff className="w-5 h-5 text-gray-400" />
                    <h3 className="font-display font-bold text-lg">Push-Benachrichtigungen</h3>
                </div>
                <p className="text-xs text-gray-500">Ihr Browser unterstützt keine Push-Benachrichtigungen. Probieren Sie Chrome, Firefox oder Edge.</p>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="push-settings">
            {/* Aug-2026 (User-Wunsch): Sektion startet eingeklappt */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 p-6 text-left hover:bg-gray-50 transition-colors"
                data-testid="push-settings-toggle"
                aria-expanded={open}
            >
                <span className="flex items-center gap-2 min-w-0">
                    {status.subscribed
                        ? <BellRing className="w-5 h-5 text-[#FFCC00] shrink-0" />
                        : <Bell className="w-5 h-5 text-[#FFCC00] shrink-0" />}
                    <span className="font-display font-bold text-lg">Push-Benachrichtigungen</span>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm shrink-0 ${status.subscribed ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"}`}>
                        {status.subscribed ? "Aktiv" : "Aus"}
                    </span>
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
            <div className="px-6 pb-6">
            <p className="text-xs text-gray-600 mb-4">
                Erhalten Sie sofortige Benachrichtigungen auf diesem Gerät — auch wenn der TwoWheels-Tab geschlossen ist.
            </p>

            {!vapidConfigured && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="push-vapid-warning">
                    <AlertCircle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                    <div className="text-xs text-amber-900">
                        Der Push-Server ist serverseitig nicht konfiguriert. Bitte VAPID-Schlüssel in der Backend-Konfiguration setzen.
                    </div>
                </div>
            )}

            {/* Master-Toggle */}
            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-sm mb-4">
                <div>
                    <div className="font-bold text-sm">
                        {status.subscribed ? "Aktiviert" : "Nicht aktiviert"} auf diesem Gerät
                    </div>
                    <div className="text-[11px] text-gray-500">
                        Berechtigung: {status.permission === "granted" ? "erlaubt" : status.permission === "denied" ? "verweigert" : "nicht angefragt"}
                    </div>
                </div>
                {status.subscribed ? (
                    <Button
                        onClick={disable}
                        disabled={busy}
                        variant="outline"
                        size="sm"
                        className="rounded-sm font-bold uppercase tracking-wider text-xs text-red-700 border-red-300 hover:bg-red-50"
                        data-testid="push-disable"
                    >
                        {busy ? "…" : "Deaktivieren"}
                    </Button>
                ) : (
                    <Button
                        onClick={enable}
                        disabled={busy || !vapidConfigured || status.permission === "denied"}
                        size="sm"
                        className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs"
                        data-testid="push-enable"
                    >
                        {busy ? "…" : "Aktivieren"}
                    </Button>
                )}
            </div>

            {/* Geräte-Verwaltung: jedes Browser/Gerät hat ein eigenes Abo */}
            {devices.length > 0 && (
                <div className="mb-4" data-testid="push-devices">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">
                        Angemeldete Geräte ({devices.length})
                    </div>
                    <div className="space-y-1">
                        {devices.map((d) => {
                            const isCurrent = d.endpoint === status.endpoint;
                            return (
                                <div
                                    key={d.endpoint}
                                    className="flex items-center justify-between gap-2 p-2 border border-gray-200 rounded-sm"
                                    data-testid="push-device-row"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold text-[#111827] flex items-center gap-2">
                                            {deviceLabel(d.user_agent)}
                                            {isCurrent && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm">
                                                    Dieses Gerät
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-gray-500">
                                            Angemeldet: {d.created_at ? new Date(d.created_at).toLocaleDateString("de-DE") : "—"}
                                        </div>
                                    </div>
                                    <Button
                                        onClick={() => removeDevice(d.endpoint)}
                                        variant="ghost"
                                        size="sm"
                                        className="rounded-sm text-xs font-bold uppercase tracking-wider text-red-700 hover:bg-red-50 shrink-0"
                                        data-testid="push-device-remove"
                                    >
                                        Entfernen
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">
                        Hinweis: Push-Benachrichtigungen müssen auf jedem Gerät und für jede Web-Adresse separat aktiviert
                        werden. Entfernte Geräte erhalten keine Benachrichtigungen mehr von diesem System.
                    </p>
                </div>
            )}

            {/* Testbenachrichtigung */}
            {devices.length > 0 && (
                <div className="mb-4">
                    <Button
                        onClick={sendTest}
                        disabled={busy}
                        variant="outline"
                        size="sm"
                        className="rounded-sm font-bold uppercase tracking-wider text-xs"
                        data-testid="push-send-test"
                    >
                        {busy ? "…" : "Testbenachrichtigung senden"}
                    </Button>
                </div>
            )}

            {/* Per-Kategorie-Toggles */}
            {status.subscribed && (
                <div className="space-y-1" data-testid="push-categories">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">
                        Welche Push-Benachrichtigungen möchten Sie erhalten?
                    </div>
                    {CATEGORY_LABELS.map((cat) => {
                        const enabled = !disabledCats.includes(cat.key);
                        return (
                            <label
                                key={cat.key}
                                className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-sm cursor-pointer"
                                data-testid={`push-cat-${cat.key}`}
                            >
                                <Switch
                                    checked={enabled}
                                    onCheckedChange={() => toggleCategory(cat.key, enabled)}
                                    className="mt-0.5"
                                    data-testid={`push-cat-switch-${cat.key}`}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-[#111827]">{cat.label}</div>
                                    <div className="text-[11px] text-gray-500">{cat.desc}</div>
                                </div>
                            </label>
                        );
                    })}
                </div>
            )}
            </div>
            )}
        </div>
    );
}
